import React, { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Col, Form, Modal, Row, Typography } from 'antd';

import { useDefaultFormErrorNotification, useDisclosure } from '@/hooks';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import ProfileForm from '@/components/Settings/Profiles/ProfileForm';

import {
  useProfile,
  useRemoveProfile,
  useUpdateProfile,
} from '@/core/domain/enterprise/Profiles/Profiles.hooks';
import { PageProps } from '@/types/PageProps';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { SaveButton } from '@/components/_common';
import ButtonBack from '@/components/_common/ButtonBack';
import DuplicateProfilesModal from '@/components/Settings/Profiles/DuplicateProfilesModal/inedx';

const { Title } = Typography;

export interface FormValues {
  name: string;
  rolesId: number[];
}

const ProfileDetailsPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<FormValues>();
  const { profileId } = useRouteMatch<{ profileId: string }>().params;
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const [checkeds, setCheckeds] = useState<number[]>([]);

  const [modalOpened, modalAction] = useDisclosure();

  const updateMutation = useUpdateProfile();
  const removeMutation = useRemoveProfile();

  const { data: profile, dataUpdatedAt } = useProfile(Number(profileId));

  const { setParamLabel } = useBreadcrumbs();

  useEffect(() => {
    if (profile) {
      setParamLabel(':profileId', profile.name);

      form.setFieldsValue({
        name: profile.name,
      });
      setCheckeds(profile.roles.map((r) => r.id));
    }
  }, [dataUpdatedAt]);

  const handleSaveClick = async () => {
    try {
      const values = await form.validateFields();

      const obj = {
        ...values,
        rolesId: checkeds,
        id: Number(profileId),
      };

      await updateMutation.mutateAsync(obj);
    } catch (err) {
      sendFormErrorNotification(err);
    }
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'profiles.remove' }),
      content: `${intl.formatMessage({
        id: 'profiles.remove.alert',
      })} ${profile?.name}`,
      onOk: async () => {
        await removeMutation.mutateAsync(Number(profileId));

        history.push('/app/settings/profiles');
      },
      okType: 'danger',
    });
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={3}>
            <FormattedMessage id={route.name} />
          </Title>
        </Col>

        <Col span={24}>
          <SpaceForButton justifyContent="end">
            <ButtonBack path="/app/settings/profiles" />

            <Button
              type="primary"
              block
              onClick={() => history.push('/app/settings/profiles/create')}
            >
              <FormattedMessage id="profiles.create" />
            </Button>

            <Button block onClick={modalAction.open}>
              Replicar grupo
            </Button>

            <Button
              danger
              block
              onClick={handleRemoveClick}
              loading={removeMutation.isLoading}
            >
              <FormattedMessage id="commons.entity.remove" />
            </Button>

            <SaveButton
              block
              type="primary"
              onClick={handleSaveClick}
              loading={updateMutation.isLoading}
            >
              <FormattedMessage id="commons.entity.save" />
            </SaveButton>
          </SpaceForButton>
        </Col>

        <Col span={24}>
          <ProfileForm
            form={form}
            roleIds={checkeds}
            onChangeRoleIds={setCheckeds}
            isAdm={profile?.system}
          />
        </Col>
      </Row>

      <DuplicateProfilesModal
        profileId={profile?.id}
        profileName={profile?.name}
        open={modalOpened}
        onCancel={modalAction.close}
      />
    </>
  );
};

export default ProfileDetailsPage;
